import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LIVE_HOME_PATH } from "./constants";
import { clearStagingSession, readCycleId, writeCycleId } from "./session";
import type { StagingPreviewContextValue } from "./types";

const StagingPreviewContext = createContext<StagingPreviewContextValue | null>(null);

/**
 * Provider for the storefront's staging-preview state.
 *
 * <p>Reads the cycle id from sessionStorage on mount so a hard reload after
 * entering staging stays in staging mode (until explicit exit or 410).
 *
 * <p>Also listens for cross-tab sessionStorage changes via the {@code storage}
 * event — exiting staging in one tab flushes the state in all tabs that share
 * the same origin.
 */
export function StagingPreviewProvider({ children }: { children: React.ReactNode }) {
  const [cycleId, setCycleId] = useState<string | null>(() => readCycleId());

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === null) {
        setCycleId(null);
        return;
      }
      if (e.key === "germanos.staging.cycleId") {
        setCycleId(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const enterStaging = useCallback((nextCycleId: string) => {
    if (!nextCycleId) return;
    writeCycleId(nextCycleId);
    setCycleId(nextCycleId);
  }, []);

  /**
   * Exit staging preview in three precise steps, in order:
   * <ol>
   *   <li>Flush the ENTIRE staging session via {@link clearStagingSession} —
   *       cycle id + HMAC token AND the pre-staging slot + project name. The
   *       slot has its own lifecycle, so clearing only the cycle would leave
   *       {@code germanos.staging.slot} behind: the banner unmounts (it gates
   *       on the cycle) while the fetch interceptor keeps sending
   *       {@code X-Project-Slot}, leaking the slot's data into the live session
   *       in this tab until it's closed. Any new tab / hard reload now lands in
   *       a fully clean live mode.
   *   <li>Flip the in-memory context state so any still-mounted component that
   *       reacts synchronously (the banner, the head tags) unmounts before the
   *       navigation fires.
   *   <li>Full-page navigate (NOT React Router SPA-nav) to the live home path.
   *       A soft navigation would leak in-memory caches populated during the
   *       preview (React Query, module-level singletons, async-store slices)
   *       into the live session; a hard assign forces a fresh document, and
   *       therefore a fresh JS VM, so nothing staging-tainted survives.
   * </ol>
   */
  const exitStaging = useCallback(() => {
    clearStagingSession();
    setCycleId(null);
    if (typeof window !== "undefined") {
      window.location.assign(LIVE_HOME_PATH);
    }
  }, []);

  const value = useMemo<StagingPreviewContextValue>(
    () => ({
      cycleId,
      isStaging: cycleId !== null,
      enterStaging,
      exitStaging,
    }),
    [cycleId, enterStaging, exitStaging]
  );

  return <StagingPreviewContext.Provider value={value}>{children}</StagingPreviewContext.Provider>;
}

/** Hook — throws if used outside the provider (common pattern, easier bug-hunt). */
export function useStagingPreview(): StagingPreviewContextValue {
  const ctx = useContext(StagingPreviewContext);
  if (ctx === null) {
    throw new Error("useStagingPreview must be used within StagingPreviewProvider");
  }
  return ctx;
}
